import type { FC } from '../../../lib/teact/teact';
import { useEffect, useMemo, useRef } from '../../../lib/teact/teact';
import { getActions, withGlobal } from '../../../global';

import type {
  ApiAudio, ApiChat, ApiMessage, ApiPeer,
  MediaContent,
} from '../../../api/types';
import type { IconName } from '../../../types/icons';

import { PLAYBACK_RATE_FOR_AUDIO_MIN_DURATION } from '../../../config';
import { requestMutation } from '../../../lib/fasterdom/fasterdom';
import {
  getMediaFormat,
  getMessageContent, isMessageLocal,
} from '../../../global/helpers';
import { getPeerTitle } from '../../../global/helpers/peers';
import {
  selectChat, selectChatMessage, selectSender, selectTabState,
} from '../../../global/selectors';
import { selectMessageMediaDuration } from '../../../global/selectors/media';
import { makeTrackId } from '../../../util/audioPlayer';
import { IS_IOS, IS_TOUCH_ENV } from '../../../util/browser/windowEnvironment';
import buildClassName from '../../../util/buildClassName';
import { isUserId } from '../../../util/entities/ids';
import { clearMediaSession } from '../../../util/mediaSession';
import renderText from '../../common/helpers/renderText';

import useMessageMediaHash from '../../../hooks/media/useMessageMediaHash';
import useAppLayout from '../../../hooks/useAppLayout';
import useAudioPlayer from '../../../hooks/useAudioPlayer';
import useContextMenuHandlers from '../../../hooks/useContextMenuHandlers';
import useCurrentOrPrev from '../../../hooks/useCurrentOrPrev';
import useLang from '../../../hooks/useLang';
import useLastCallback from '../../../hooks/useLastCallback';
import useMedia from '../../../hooks/useMedia';
import useMessageMediaMetadata from '../../../hooks/useMessageMediaMetadata';
import useOldLang from '../../../hooks/useOldLang';
import useShowTransition from '../../../hooks/useShowTransition';
import useHeaderPane, { type PaneState } from '../hooks/useHeaderPane';

import Icon from '../../common/icons/Icon';
import Button from '../../ui/Button';
import DropdownMenu from '../../ui/DropdownMenu';
import MenuItem from '../../ui/MenuItem';
import RangeSlider from '../../ui/RangeSlider';
import RippleEffect from '../../ui/RippleEffect';

import './AudioPlayer.scss';

type OwnProps = {
  className?: string;
  noUi?: boolean;
  isFullWidth?: boolean;
  isInSharedMedia?: boolean;
  isHidden?: boolean;
  onPaneStateChange?: (state: PaneState) => void;
};

type StateProps = {
  message?: ApiMessage;
  sender?: ApiPeer;
  chat?: ApiChat;
  mediaDuration?: number;
  volume: number;
  playbackRate: number;
  isPlaybackRateActive?: boolean;
  isMuted: boolean;
  timestamp?: number;
};

// Each level plays at exactly the speed its label shows
const PLAYBACK_RATE_VALUES = [0.5, 0.75, 1, 1.5, 2];

const REGULAR_PLAYBACK_RATE = 1;
// Tapping the speed button steps through these; any other level jumps to the next one above it
const PLAYBACK_RATE_CYCLE = [1, 1.5, 2];

const AudioPlayer: FC<OwnProps & StateProps> = ({
  message,
  mediaDuration,
  className,
  noUi,
  sender,
  chat,
  volume,
  playbackRate,
  isPlaybackRateActive,
  isMuted,
  isFullWidth,
  isInSharedMedia,
  timestamp,
  onPaneStateChange,
}) => {
  const {
    setAudioPlayerVolume,
    setAudioPlayerPlaybackRate,
    setAudioPlayerMuted,
    focusMessage,
    closeAudioPlayer,
    showNotification,
  } = getActions();

  const oldLang = useOldLang();
  const lang = useLang();

  const { isMobile } = useAppLayout();
  const renderingMessage = useCurrentOrPrev(message);

  const { audio, voice, video } = renderingMessage ? getMessageContent(renderingMessage) : {} satisfies MediaContent;
  // A training recording picked from storage is `audio`, yet it is shown and played as a voice message
  const isVoice = Boolean(voice || video || audio?.isTrainingRecording);
  const shouldRenderPlaybackButton = isVoice || (audio?.duration || 0) > PLAYBACK_RATE_FOR_AUDIO_MIN_DURATION;
  const senderName = sender ? getPeerTitle(oldLang, sender) : undefined;
  const voiceSubtitle = (chat && !isUserId(chat.id) && getPeerTitle(oldLang, chat)) || oldLang('AttachAudio');
  const playbackLevel = getPlaybackLevel(playbackRate, isPlaybackRateActive);

  const playableMedia = voice || video || audio;
  const mediaHash = useMessageMediaHash(renderingMessage, 'inline');
  const mediaFormat = playableMedia && getMediaFormat(playableMedia, 'inline');
  const mediaData = useMedia(mediaHash, false, mediaFormat);
  const mediaMetadata = useMessageMediaMetadata(renderingMessage, sender, chat);

  const progressRef = useRef<HTMLDivElement>();

  // The line follows the audio directly, since re-rendering the whole bar on every time update is costly
  const handleTimeUpdate = useLastCallback((e: Event) => {
    updateProgressLine(progressRef.current, e.currentTarget as HTMLAudioElement, mediaDuration);
  });

  const {
    playPause,
    stop,
    isPlaying,
    audioProxy,
    requestNextTrack,
    requestPreviousTrack,
    isFirst,
    isLast,
    setVolume,
    toggleMuted,
    setPlaybackRate,
    setCurrentTime,
  } = useAudioPlayer(
    message && makeTrackId(message),
    mediaDuration || 0,
    isVoice ? 'voice' : 'audio',
    mediaData,
    { onTimeUpdate: handleTimeUpdate },
    mediaMetadata,
    undefined,
    true,
    undefined,
    undefined,
    message && isMessageLocal(message),
    true,
  );

  const isOpen = Boolean(message);
  const {
    ref: transitionRef,
  } = useShowTransition({
    isOpen,
    shouldForceOpen: isFullWidth, // Use pane animation instead
  });

  const { ref, shouldRender } = useHeaderPane({
    isOpen,
    isDisabled: !isFullWidth,
    ref: transitionRef,
    onStateChange: onPaneStateChange,
  });

  const {
    isContextMenuOpen,
    handleBeforeContextMenu, handleContextMenu,
    handleContextMenuClose, handleContextMenuHide,
  } = useContextMenuHandlers(transitionRef, !shouldRender);

  // A new track, or a bar shown again, starts from where that audio is rather than from the previous line
  useEffect(() => {
    if (audioProxy) updateProgressLine(progressRef.current, audioProxy, mediaDuration);
  }, [audioProxy, mediaDuration, shouldRender]);

  useEffect(() => {
    if (timestamp) {
      setCurrentTime(timestamp);
    }
  }, [timestamp, setCurrentTime]);

  useEffect(() => {
    if (isPlaying && message?.isDeleting) {
      playPause();
    }
  }, [isPlaying, message?.isDeleting, playPause]);

  const handleClick = useLastCallback(() => {
    const { chatId, id } = renderingMessage!;
    focusMessage({ chatId, messageId: id });
  });

  const handleClose = useLastCallback(() => {
    if (!stop) {
      return;
    }
    if (isPlaying) {
      playPause();
    }
    closeAudioPlayer();
    clearMediaSession();
    stop();
  });

  const handleVolumeChange = useLastCallback((value: number) => {
    if (!setVolume) {
      return;
    }
    setAudioPlayerVolume({ volume: value / 100 });
    setVolume(value / 100);
  });

  const handleVolumeClick = useLastCallback(() => {
    if (IS_TOUCH_ENV && !IS_IOS) return;
    if (!toggleMuted) {
      return;
    }
    toggleMuted();
    setAudioPlayerMuted({ isMuted: !isMuted });
  });

  const updatePlaybackRate = useLastCallback((level: number) => {
    if (!setPlaybackRate) {
      return;
    }
    setAudioPlayerPlaybackRate({ playbackRate: level, isPlaybackRateActive: level !== REGULAR_PLAYBACK_RATE });
    setPlaybackRate(level);
    showNotification({ message: { key: 'PlayerPlaybackRateToast', variables: { rate: level } } });
  });

  const handlePlaybackClick = useLastCallback(() => {
    handleContextMenuClose();
    updatePlaybackRate(PLAYBACK_RATE_CYCLE.find((level) => level > playbackLevel) ?? PLAYBACK_RATE_CYCLE[0]);
  });

  const PlaybackRateButton = useLastCallback(() => {
    const text = `${playbackLevel}X`;
    return (
      <div className="playback-wrapper">
        {isContextMenuOpen && <div className="playback-backdrop" onClick={handleContextMenuClose} />}

        <Button
          round
          className={buildClassName(
            'playback-button', playbackLevel !== REGULAR_PLAYBACK_RATE && 'applied', isContextMenuOpen && 'on-top',
          )}
          color="translucent"
          size="smaller"
          ariaLabel={lang('PlayerPlaybackRate')}
          ripple={!isMobile}
          onMouseEnter={handleContextMenu}
          onClick={handlePlaybackClick}
          onMouseDown={handleBeforeContextMenu}
          onContextMenu={handleContextMenu}
        >
          <span className={buildClassName(
            'playback-button-inner',
            text.length === 4 && 'small',
            text.length === 5 && 'tiny',
          )}
          >
            {text}
          </span>
        </Button>
      </div>
    );
  });

  const volumeIcon: IconName = useMemo(() => {
    if (volume === 0 || isMuted) return 'muted';
    if (volume < 0.3) return 'volume-1';
    if (volume < 0.6) return 'volume-2';
    return 'volume-3';
  }, [volume, isMuted]);

  if (noUi || !shouldRender) {
    return undefined;
  }

  return (
    <div
      className={buildClassName(
        'AudioPlayer',
        isFullWidth ? 'full-width-player' : 'mini-player',
        isInSharedMedia && 'shared-media-player',
        className,
      )}
      dir={oldLang.isRtl ? 'rtl' : undefined}
      ref={ref}
    >
      <div className="AudioPlayer-content" onClick={handleClick}>
        {audio && !isVoice ? renderAudio(audio) : renderVoice(voiceSubtitle, senderName)}
        <RippleEffect />
      </div>

      {/* The tab bar is narrow, so it keeps only the controls the spec asks for */}
      {!isInSharedMedia && (
        <Button
          round
          ripple={!isMobile}
          color="translucent"
          size="smaller"
          className="player-button"
          disabled={isFirst?.()}
          onClick={requestPreviousTrack}
          ariaLabel={lang('AudioPlayerPrevious')}
          iconName="skip-previous"
        />
      )}
      <Button
        round
        ripple={!isMobile}
        color="translucent"
        size="smaller"
        className={buildClassName('toggle-play', 'player-button', isPlaying ? 'pause' : 'play')}
        onClick={playPause}
        ariaLabel={isPlaying ? 'Pause audio' : 'Play audio'}
      >
        <Icon name="play" />
        <Icon name="pause" />
      </Button>
      {!isInSharedMedia && (
        <Button
          round
          ripple={!isMobile}
          color="translucent"
          size="smaller"
          className="player-button"
          disabled={isLast?.()}
          onClick={requestNextTrack}
          ariaLabel={lang('AudioPlayerNext')}
          iconName="skip-next"
        />
      )}

      {!isInSharedMedia && (
        <div className="volume-button-wrapper">
          <Button
            round
            className="player-button volume-button"
            color="translucent"
            size="smaller"
            ariaLabel={lang('PlayerVolume')}
            onClick={handleVolumeClick}
            ripple={!isMobile}
            iconName={volumeIcon}
          />

          {!IS_IOS && (
            <div className="volume-slider-wrapper">
              <div className="volume-slider-spacer" />
              <div className="volume-slider">
                <RangeSlider bold value={isMuted ? 0 : volume * 100} onChange={handleVolumeChange} />
              </div>
            </div>
          )}
        </div>
      )}

      {shouldRenderPlaybackButton && (
        <DropdownMenu
          forceOpen={isContextMenuOpen}
          positionX="right"
          positionY="top"
          className="playback-rate-menu"
          trigger={PlaybackRateButton}
          onClose={handleContextMenuClose}
          onHide={handleContextMenuHide}
          onMouseEnterBackdrop={handleContextMenuClose}
        >
          {PLAYBACK_RATE_VALUES.map((rate) => {
            return renderPlaybackRateMenuItem(rate, playbackLevel, updatePlaybackRate);
          })}
        </DropdownMenu>
      )}

      <Button
        round
        className="player-close"
        color="translucent"
        size="smaller"
        onClick={handleClose}
        ariaLabel={lang('AudioPlayerClose')}
        iconName="close"
      />
      <div ref={progressRef} className="player-progress" />
    </div>
  );
};

function renderAudio(audio: ApiAudio) {
  const { title, performer, fileName } = audio;

  return (
    <>
      <div className="title" dir="auto">{renderText(title || fileName)}</div>
      {performer && (
        <div className="subtitle" dir="auto">{renderText(performer)}</div>
      )}
    </>
  );
}

function renderVoice(subtitle: string, senderName?: string) {
  return (
    <>
      <div className="title" dir="auto">{senderName && renderText(senderName)}</div>
      <div className="subtitle" dir="auto">{subtitle}</div>
    </>
  );
}

function updateProgressLine(element: HTMLDivElement | undefined, audio: HTMLAudioElement, fallbackDuration?: number) {
  const duration = Number.isFinite(audio.duration) && audio.duration ? audio.duration : fallbackDuration;
  const progress = duration ? Math.min(audio.currentTime / duration, 1) : 0;

  requestMutation(() => {
    if (element) element.style.transform = `scaleX(${progress})`;
  });
}

function getPlaybackLevel(playbackRate: number, isPlaybackRateActive?: boolean) {
  if (!isPlaybackRateActive) return REGULAR_PLAYBACK_RATE;

  return PLAYBACK_RATE_VALUES.includes(playbackRate) ? playbackRate : REGULAR_PLAYBACK_RATE;
}

function renderPlaybackRateMenuItem(rate: number, currentLevel: number, onClick: (rate: number) => void) {
  const isSelected = rate === currentLevel;
  return (
    <MenuItem
      key={rate}

      onClick={() => onClick(rate)}
      icon={isSelected ? 'check' : undefined}
      customIcon={!isSelected ? <Icon name="placeholder" /> : undefined}
    >
      {rate}
      X
    </MenuItem>
  );
}

export default withGlobal<OwnProps>(
  (global, { isHidden }): Complete<StateProps> => {
    const { audioPlayer } = selectTabState(global);
    const { chatId, messageId } = audioPlayer;
    const message = !isHidden && chatId && messageId ? selectChatMessage(global, chatId, messageId) : undefined;

    const sender = message && selectSender(global, message);
    const chat = message && selectChat(global, message.chatId);
    const {
      playbackRate, isMuted, isPlaybackRateActive, timestamp,
    } = selectTabState(global).audioPlayer;
    const { volume } = global.audioPlayer;

    const mediaDuration = message ? selectMessageMediaDuration(global, message) : undefined;

    return {
      message,
      sender,
      chat,
      volume,
      playbackRate,
      isPlaybackRateActive,
      isMuted,
      timestamp,
      mediaDuration,
    };
  },
)(AudioPlayer);
